import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
} from 'react';

import { useQueryClient } from '@tanstack/react-query';
import posthog from 'posthog-js';

import useAnalyzedEmail from '@/features/analysis/queries/hooks/useAnalyzedEmail';
import type { EmailAnalysis, EmailMessage } from '@/features/email/types';
import type { PartyData } from '@/features/party/types';
import queryKeys from '@/queries/queryKeys';
import useLinkedPartyStore from '@/stores/useLinkedPartyStore';

import useAnalysis from './hooks/useAnalysis';
import useEmailData from './hooks/useEmailData';
import usePartyData from './hooks/usePartyData';

type EmailContextType = {
  email: Maybe<EmailMessage>;
  party: PartyData;
  analysis: EmailAnalysis;
  isPartySelected: boolean;
  isEmailSelected: boolean;
  linkParty: (partyId: string) => void;
  forceAnalyze: () => void;
  reAnalyze: () => void;
};

const EmailContext = createContext<EmailContextType | null>(null);

export default function EmailProvider({ children }: PropsWithChildren) {
  const queryClient = useQueryClient();
  const email = useEmailData();

  const shouldFetchAnalyzedEmail = Boolean(email?.messageId);
  const analyzedEmailQuery = useAnalyzedEmail(email?.messageId ?? '', {
    enabled: shouldFetchAnalyzedEmail,
  });

  const party = usePartyData(email, analyzedEmailQuery.data);
  const analysis = useAnalysis(email, analyzedEmailQuery, party);

  const { linkPartyForEmail } = useLinkedPartyStore((state) => state.actions);

  const linkParty = useCallback(
    (partyId: string) => {
      if (!email?.messageId) return;

      posthog.capture('User manually linked party to email', {
        messageId: email.messageId,
        partyId,
      });

      queryClient.removeQueries({ queryKey: queryKeys.onesurance.all() });

      linkPartyForEmail(email.messageId, partyId);
    },
    [email?.messageId, linkPartyForEmail, queryClient],
  );

  const value = {
    email,
    party,
    analysis,

    isEmailSelected: Boolean(email),
    isPartySelected: Boolean(party.party),

    linkParty,
    forceAnalyze: analysis.forceAnalyze,
    reAnalyze: analysis.reAnalyze,
  };

  return (
    <EmailContext.Provider value={value}>{children}</EmailContext.Provider>
  );
}

export const useEmail = (): EmailContextType => {
  const context = useContext(EmailContext);

  if (!context) {
    throw new Error('useEmail must be used within a EmailProvider');
  }

  return context;
};
